/**
 * The navigation model: the one source the sidebar and the router both read.
 *
 * Which entries a caller sees is the server's decision (the `navigation` list
 * in `/api/auth/me`, from backend/app/services/navigation.py). Each feature
 * that ships a screen adds one entry here and one there.
 */

import type { LucideIcon } from 'lucide-react';
import {
  BriefcaseBusiness,
  Compass,
  Factory,
  Gauge,
  LayoutDashboard,
  Library,
  Lightbulb,
  MessagesSquare,
  ScrollText,
  Settings,
  Sparkle,
  Target,
  Users,
  Waypoints,
} from 'lucide-react';

export type NavGroup = null | 'DISCOVERY' | 'ASSETS' | 'REFERENCE_DATA' | 'MANAGEMENT' | 'MONITOR' | 'SUPPORT';

export interface NavEntry {
  key: string;
  label: string;
  /** Where the entry routes; null for an entry that opens a dialog in the shell instead. */
  path: string | null;
  icon: LucideIcon;
  group: NavGroup;
}

export const NAV_ENTRIES: readonly NavEntry[] = [
  { key: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, group: null },
  { key: 'submit-idea', label: 'Submit an Idea', path: null, icon: Lightbulb, group: null },
  // Business-opportunity research over the submitted ideas; other kinds of research get entries of their own.
  {
    key: 'business-opportunities',
    label: 'Business Opportunities',
    path: '/business-opportunities',
    icon: BriefcaseBusiness,
    group: 'DISCOVERY',
  },
  // The asset catalog: the list, and every asset's page and editor beneath it (/assets/<id>).
  { key: 'assets', label: 'Catalog', path: '/assets', icon: Library, group: 'ASSETS' },
  // The Asset Advisor: the runs list, and each run's result page beneath it (/advisor/<id>).
  { key: 'advisor', label: 'Asset Advisor', path: '/advisor', icon: Compass, group: 'ASSETS' },
  // The needs nothing in the catalog matched strongly, for portfolio and offer owners.
  { key: 'unmatched-demand', label: 'Unmatched demand', path: '/unmatched-demand', icon: Target, group: 'ASSETS' },
  // Reference data research classifies by and draws on, kept apart from what is researched.
  // Everyone reads it; the edit controls follow permission keys.
  { key: 'industries', label: 'Industries', path: '/industries', icon: Factory, group: 'REFERENCE_DATA' },
  { key: 'users', label: 'Team & Access', path: '/users', icon: Users, group: 'MANAGEMENT' },
  // AI models, budget and Business Opportunities are tabs on this page (CONFIG_TABS in ConfigurationPage).
  { key: 'configuration', label: 'Configuration', path: '/configuration', icon: Settings, group: 'MANAGEMENT' },
  { key: 'ai-usage', label: 'AI Usage', path: '/ai-usage', icon: Gauge, group: 'MONITOR' },
  { key: 'audit-log', label: 'Audit Log', path: '/audit-log', icon: ScrollText, group: 'MONITOR' },
  // Bugs, enhancements, suggestions and questions: everyone files and follows their own, admins triage all.
  { key: 'feedback', label: 'Feedback', path: '/feedback', icon: MessagesSquare, group: 'SUPPORT' },
  // API keys, the integration API's reference and guide, and MCP access: how other systems reach the app as you.
  { key: 'integrations', label: 'Integrations', path: '/integrations', icon: Waypoints, group: 'SUPPORT' },
  // The release notes of the version that runs, and the earlier ones.
  { key: 'whats-new', label: "What's new", path: '/whats-new', icon: Sparkle, group: 'SUPPORT' },
];

export const NAV_GROUP_ORDER: readonly NavGroup[] = [null, 'DISCOVERY', 'ASSETS', 'REFERENCE_DATA', 'MANAGEMENT', 'MONITOR', 'SUPPORT'];

export const GROUP_LABELS: Readonly<Record<string, string>> = {
  DISCOVERY: 'Discovery',
  ASSETS: 'Assets',
  REFERENCE_DATA: 'Reference Data',
  MANAGEMENT: 'Management',
  MONITOR: 'Monitor',
  SUPPORT: 'Support',
};

export function entriesInGroup(group: NavGroup): NavEntry[] {
  return NAV_ENTRIES.filter((entry) => entry.group === group);
}

/** The entry a path belongs to. The most specific wins, so an idea's own page (/business-opportunities/<id>) stays under the matrix. */
export function activeNavKey(pathname: string): string | undefined {
  return NAV_ENTRIES.filter((e) => e.path && (pathname === e.path || pathname.startsWith(`${e.path}/`))).sort(
    (a, b) => (b.path?.length ?? 0) - (a.path?.length ?? 0),
  )[0]?.key;
}
